import React, { useCallback, useState } from 'react';
import moment, { Moment } from 'moment-timezone';
import { noop } from 'lodash';
import { Select, Row, Col } from 'antd';
import { Separator } from './styled';
interface LifeSpan {
  duration: number;
  recurrence: string;
  start: number;
}

interface IProps {
  value?: LifeSpan | null;
  onChange?: (v: LifeSpan | null) => void;
}

const { Option } = Select;

const MAX_DURATION = 60 * 60 * 24;

const calcLifeSpan = (st: Moment, et: Moment) => {
  const hoursAndMinutes = [st.hours(), st.minutes(), et.hours(), et.minutes()];
  if (hoursAndMinutes.every((v) => v === 0)) {
    return null;
  } else {
    let diff = et.diff(st, 'seconds');
    if (diff <= 0) {
      // start > end
      diff += MAX_DURATION;
    } else if (diff > MAX_DURATION) {
      // switching back from above state
      diff -= MAX_DURATION;
    }
    return {
      start: st.valueOf() / 1000, // alert config expects start time in seconds
      recurrence: 'daily',
      duration: diff,
    };
  }
};

const HoursInDay = ({ value, onChange = noop }: IProps, ref: any) => {
  const { start, duration } = (value || {}) as LifeSpan;
  // start and end hours/minutes should be added to the same 'day'
  const st = start ? moment(start * 1000) : moment().startOf('day');
  const et = duration ? moment(start * 1000).add(duration, 'seconds') : moment().startOf('day');
  const [startTime, setStartTime] = useState(st);
  const [endTime, setEndTime] = useState(et);

  const startHourChange = useCallback(
    (v) => {
      const newStartTime = moment(startTime.hours(v).format());
      onChange(calcLifeSpan(newStartTime, endTime));
      setStartTime(newStartTime);
    },
    [startTime, endTime, setStartTime, onChange],
  );

  const startMinuteChange = useCallback(
    (v) => {
      const newStartTime = moment(startTime.minutes(v).format());
      onChange(calcLifeSpan(newStartTime, endTime));
      setStartTime(newStartTime);
    },
    [startTime, endTime, setStartTime, onChange],
  );

  const endHourChange = useCallback(
    (v) => {
      const newEndTime = moment(endTime.hours(v).format());
      onChange(calcLifeSpan(startTime, newEndTime));
      setEndTime(newEndTime);
    },
    [startTime, endTime, setEndTime, onChange],
  );

  const endMinuteChange = useCallback(
    (v) => {
      const newEndTime = moment(endTime.minutes(v).format());
      onChange(calcLifeSpan(startTime, newEndTime));
      setEndTime(newEndTime);
    },
    [startTime, endTime, setEndTime, onChange],
  );

  return (
    <Row ref={ref}>
      <Col span={5}>
        <Select defaultValue={st.hours()} onChange={startHourChange}>
          {new Array(24).fill(0).map((_, idx) => (
            <Option key={idx} value={idx}>
              {`${idx}`.padStart(2, '0')}
            </Option>
          ))}
        </Select>
      </Col>
      <Col span={1}>
        <Separator>:</Separator>
      </Col>
      <Col span={5}>
        <Select defaultValue={st.minutes()} onChange={startMinuteChange}>
          {new Array(12).fill(0).map((_, idx) => (
            <Option key={idx} value={idx * 5}>
              {`${idx * 5}`.padStart(2, '0')}
            </Option>
          ))}
        </Select>
      </Col>
      <Col span={2}>
        <Separator>-</Separator>
      </Col>
      <Col span={5}>
        <Select defaultValue={et.hours()} onChange={endHourChange}>
          {new Array(24).fill(0).map((_, idx) => (
            <Option key={idx} value={idx}>
              {`${idx}`.padStart(2, '0')}
            </Option>
          ))}
        </Select>
      </Col>
      <Col span={1}>
        <Separator>:</Separator>
      </Col>
      <Col span={5}>
        <Select defaultValue={et.minutes()} onChange={endMinuteChange}>
          {new Array(12).fill(0).map((_, idx) => (
            <Option key={idx} value={idx * 5}>
              {`${idx * 5}`.padStart(2, '0')}
            </Option>
          ))}
        </Select>
      </Col>
    </Row>
  );
};

export default React.forwardRef(HoursInDay);
